import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Megaphone, RefreshCw, Search, Send } from "lucide-react";
import { getApiUrl } from "../../config/api";

interface SchoolRow {
  id: number;
  name: string;
}

interface Announcement {
  id: number;
  title: string;
  message: string;
  scope: "global" | "schools" | "roles";
  created_by_name?: string;
  created_at: string;
}

const ROLE_OPTIONS = ["admin", "headteacher", "teacher", "student"];

const BroadcastNotifications: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scope, setScope] = useState<"global" | "schools" | "roles">("global");
  const [selectedSchools, setSelectedSchools] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [schoolPickerValue, setSchoolPickerValue] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadData = async () => {
    const [announcementsRes, schoolsRes] = await Promise.all([
      axios.get(getApiUrl("superadmin/announcements"), { headers }),
      axios.get(getApiUrl("superadmin/schools"), { headers })
    ]);
    setAnnouncements(announcementsRes.data || []);
    setSchools(schoolsRes.data || []);
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSchools = useMemo(
    () => schools.filter((school) => school.name.toLowerCase().includes(schoolSearch.toLowerCase())),
    [schoolSearch, schools]
  );

  const filteredHistory = useMemo(
    () => announcements.filter((item) => `${item.title} ${item.message}`.toLowerCase().includes(historySearch.toLowerCase())),
    [announcements, historySearch]
  );

  const toggleSchool = (id: number) => {
    setSelectedSchools((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const addSchoolFromPicker = (value: string) => {
    if (!value) return;
    const parsedId = Number(value);
    if (!Number.isFinite(parsedId)) return;
    setSelectedSchools((current) => (current.includes(parsedId) ? current : [...current, parsedId]));
    setSchoolPickerValue("");
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((current) => current.includes(role) ? current.filter((item) => item !== role) : [...current, role]);
  };

  const send = async () => {
    try {
      setSending(true);
      setNotice("");
      await axios.post(getApiUrl("superadmin/announcements"), {
        title: title.trim(),
        message: message.trim(),
        scope,
        target_school_ids: scope === "schools" ? selectedSchools : undefined,
        target_roles: scope === "roles" ? selectedRoles : undefined
      }, { headers });
      setTitle("");
      setMessage("");
      setSelectedSchools([]);
      setSelectedRoles([]);
      setSchoolPickerValue("");
      setNotice("Announcement sent.");
      await loadData();
    } catch (error: any) {
      setNotice(error?.response?.data?.message || "Failed to send announcement.");
    } finally {
      setSending(false);
    }
  };

  const scopeDescription =
    scope === "global"
      ? "This message will be visible across every tenant school."
      : scope === "schools"
        ? "Only the selected schools will receive this announcement."
        : "Only users with the selected roles will receive this announcement.";

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1450px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Broadcast Center</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform-wide announcements</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Send targeted platform communications to every school, a selected set of schools, or specific user roles.
              </p>
            </div>
            <button onClick={loadData} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <Megaphone className="text-slate-700" size={20} />
              <div>
                <h2 className="text-xl font-black text-slate-900">New announcement</h2>
                <p className="text-sm text-slate-500">Compose and target your message before publishing it.</p>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              <input
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
                placeholder="Announcement title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <textarea
                className="min-h-[160px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
                placeholder="Message body"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="flex flex-wrap gap-3">
                {(["global", "schools", "roles"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setScope(option)}
                    className={`rounded-2xl border px-4 py-2 text-xs font-black uppercase tracking-[0.18em] ${scope === option ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600"}`}
                  >
                    {option === "global" ? "All schools" : option === "schools" ? "Selected schools" : "Selected roles"}
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500">{scopeDescription}</p>

              {scope === "schools" && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
                    <select
                      value={schoolPickerValue}
                      onChange={(e) => {
                        setSchoolPickerValue(e.target.value);
                        addSchoolFromPicker(e.target.value);
                      }}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-slate-300"
                    >
                      <option value="">Choose school from list</option>
                      {schools
                        .filter((school) => !selectedSchools.includes(school.id))
                        .map((school) => (
                          <option key={school.id} value={String(school.id)}>
                            {school.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setSelectedSchools(schools.map((school) => school.id))}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                    >
                      Select all
                    </button>
                  </div>
                  {selectedSchools.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {selectedSchools.map((schoolId) => {
                        const school = schools.find((entry) => entry.id === schoolId);
                        if (!school) return null;
                        return (
                          <button
                            key={school.id}
                            type="button"
                            onClick={() => toggleSchool(school.id)}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700"
                          >
                            {school.name} x
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      value={schoolSearch}
                      onChange={(e) => setSchoolSearch(e.target.value)}
                      placeholder="Search schools"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-12 py-3 text-sm outline-none transition focus:border-slate-300"
                    />
                  </div>
                  <div className="mt-4 grid max-h-[240px] gap-2 overflow-y-auto pr-1 md:grid-cols-2">
                    {filteredSchools.map((school) => (
                      <label key={school.id} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                        <input type="checkbox" checked={selectedSchools.includes(school.id)} onChange={() => toggleSchool(school.id)} />
                        <span>{school.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {scope === "roles" && (
                <div className="flex flex-wrap gap-3">
                  {ROLE_OPTIONS.map((role) => (
                    <label key={role} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} />
                      <span className="capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              )}

              <button
                onClick={send}
                disabled={sending || !title.trim() || !message.trim() || (scope === "schools" && selectedSchools.length === 0) || (scope === "roles" && selectedRoles.length === 0)}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                <Send size={16} />
                {sending ? "Sending..." : "Send announcement"}
              </button>
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-black text-slate-900">Delivery history</h2>
            <p className="text-sm text-slate-500">Review recent broadcasts and their target scope.</p>
            <div className="relative mt-5">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search announcement history"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-12 py-3 text-sm outline-none transition focus:border-slate-300 focus:bg-white"
              />
            </div>
            <div className="mt-5 space-y-3">
              {filteredHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">No announcements yet.</div>
              ) : (
                filteredHistory.map((item) => (
                  <article key={item.id} className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-black text-slate-900">{item.title}</h3>
                        <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400">{item.scope}</p>
                      </div>
                      <div className="text-xs text-slate-400">{new Date(item.created_at).toLocaleString()}</div>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-700">{item.message}</p>
                    <p className="mt-3 text-xs text-slate-500">Created by {item.created_by_name || "Superadmin"}</p>
                  </article>
                ))
              )}
            </div>
          </article>
        </section>

        {notice && <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">{notice}</div>}
      </div>
    </div>
  );
};

export default BroadcastNotifications;
